// src/components/ImmoAddPage/ImmoDashboardClient.jsx
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { useAuth } from '@/context/AuthContext';
import ChargilyPaymentPageClient from '@/features/promoteur/ChargilyPaymentPageClient';
import { db } from '@/Firebase';
import ImmoForm from './ImmoForm';

// ── Helpers ──────────────────────────────────────────────────────
function formatDate(date) {
  if (!date) return "—";
  return date.toLocaleDateString("fr-DZ", {
    day: "2-digit", month: "long", year: "numeric",
  });
}

function joursRestants(expiry) {
  if (!expiry) return 0;
  const diff = expiry.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function expiryFromCreatedAt(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt.getTime());
  d.setFullYear(d.getFullYear() + 1);
  return d;
}

// ── Badge statut ─────────────────────────────────────────────────
function StatutBadge({ status }) {
  const map = {
    approved: { label: "Validée", bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", icon: "✅" },
    pending: { label: "En attente", bg: "#fffbeb", color: "#92400e", border: "#fde68a", icon: "⏳" },
    refused: { label: "Refusée", bg: "#fef2f2", color: "#dc2626", border: "#fca5a5", icon: "❌" },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "3px 10px", borderRadius: "20px", fontSize: "12px",
      fontWeight: "600", background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
    }}>
      {s.icon} {s.label}
    </span>
  );
}

// ── Carte annonce ─────────────────────────────────────────────────
function AnnonceCard({ offer, onEdit, onDelete, processing }) {
  const createdAt = offer.createdAt?.toDate?.() || null;
  const expiresAt = offer.expiresAt?.toDate?.() || expiryFromCreatedAt(createdAt);
  const jours = joursRestants(expiresAt);
  const expired = jours === 0;

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: "16px", overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      opacity: expired ? 0.7 : 1,
    }}>
      <div style={{ position: "relative", height: "160px", background: "#f3f4f6" }}>
        {offer.mainImageUrl || offer.imagesUrls?.[0] ? (
          <img src={offer.mainImageUrl || offer.imagesUrls[0]} alt={offer.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "40px", color: "#d1d5db" }}>🏠</div>
        )}
        <div style={{ position: "absolute", top: "10px", right: "10px" }}>
          <StatutBadge status={offer.status} />
        </div>
        {expired && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontWeight: "700", fontSize: "14px", background: "#dc2626", padding: "6px 14px", borderRadius: "20px" }}>⏰ Expirée</span>
          </div>
        )}
      </div>

      <div style={{ padding: "16px" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: "700", color: "#111827" }}>{offer.name || "Sans titre"}</h3>
        <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6b7280" }}>📍 {offer.location || "—"}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "14px" }}>
          <div style={infoBoxStyle}>
            <span style={infoLabelStyle}>Prix</span>
            <span style={infoValueStyle}>{offer.price ? `${Number(offer.price).toLocaleString("fr-DZ")} DA` : "—"}</span>
          </div>
          <div style={infoBoxStyle}>
            <span style={infoLabelStyle}>Surface</span>
            <span style={infoValueStyle}>{offer.area || "—"}</span>
          </div>
          <div style={infoBoxStyle}>
            <span style={infoLabelStyle}>Publiée le</span>
            <span style={infoValueStyle}>{formatDate(createdAt)}</span>
          </div>
          <div style={infoBoxStyle}>
            <span style={infoLabelStyle}>Expire le</span>
            <span style={{ ...infoValueStyle, color: jours <= 30 ? "#dc2626" : "#111827" }}>{formatDate(expiresAt)}</span>
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ fontSize: "11px", color: "#9ca3af" }}>Durée de l'annonce</span>
            <span style={{ fontSize: "11px", fontWeight: "600", color: jours <= 30 ? "#dc2626" : "#15803d" }}>{jours} jours restants</span>
          </div>
          <div style={{ background: "#e5e7eb", borderRadius: "99px", height: "6px", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: "99px", background: jours <= 30 ? "#ef4444" : "#16a34a", width: `${Math.min(100, (jours / 365) * 100)}%` }} />
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => onEdit(offer.id)} disabled={processing} style={btnSecondaryStyle}>✏️ Modifier</button>
          <button
            onClick={() => { if (window.confirm("Supprimer cette annonce ? Vous devrez repayer pour en publier une nouvelle.")) onDelete(offer.id); }}
            disabled={processing}
            style={btnDangerStyle}
          >
            🗑️ Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

const infoBoxStyle = { background: "#f9fafb", borderRadius: "8px", padding: "8px 10px", display: "flex", flexDirection: "column", gap: "2px" };
const infoLabelStyle = { fontSize: "11px", color: "#9ca3af", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.4px" };
const infoValueStyle = { fontSize: "13px", color: "#111827", fontWeight: "600" };
const btnSecondaryStyle = { flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: "13px", fontWeight: "600", cursor: "pointer" };
const btnDangerStyle = { flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #fca5a5", background: "#fef2f2", color: "#dc2626", fontSize: "13px", fontWeight: "600", cursor: "pointer" };

// ── Résumé tableau de bord ────────────────────────────────────────
function AbonnementResume({ credits, totalPaid, offers }) {
  const approved = offers.filter(o => o.status === "approved").length;
  const pending = offers.filter(o => o.status === "pending").length;

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "24px" }}>
      <h2 style={{ margin: "0 0 20px", fontSize: "17px", fontWeight: "700", color: "#111827" }}>📊 Tableau de bord</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
        <div style={{ background: credits > 0 ? "#f0fdf4" : "#fef2f2", border: `1px solid ${credits > 0 ? "#bbf7d0" : "#fca5a5"}`, borderRadius: "14px", padding: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "28px", fontWeight: "800", color: credits > 0 ? "#15803d" : "#dc2626" }}>{credits}</div>
          <div style={{ fontSize: "12px", color: credits > 0 ? "#15803d" : "#dc2626", fontWeight: "600", marginTop: "4px" }}>crédit{credits > 1 ? "s" : ""} disponible{credits > 1 ? "s" : ""}</div>
          {credits === 0 && <div style={{ fontSize: "11px", color: "#dc2626", marginTop: "4px" }}>Payez pour publier</div>}
        </div>
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "14px", padding: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "28px", fontWeight: "800", color: "#1d4ed8" }}>{totalPaid}</div>
          <div style={{ fontSize: "12px", color: "#1d4ed8", fontWeight: "600", marginTop: "4px" }}>paiements effectués</div>
        </div>
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "28px", fontWeight: "800", color: "#15803d" }}>{approved}</div>
          <div style={{ fontSize: "12px", color: "#15803d", fontWeight: "600", marginTop: "4px" }}>✅ validées</div>
        </div>
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "14px", padding: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "28px", fontWeight: "800", color: "#92400e" }}>{pending}</div>
          <div style={{ fontSize: "12px", color: "#92400e", fontWeight: "600", marginTop: "4px" }}>⏳ en attente</div>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────
const ImmoDashboardClient = () => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showPayment, setShowPayment] = useState(false); // 🆕

  const [subLoading, setSubLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [hasEverPaid, setHasEverPaid] = useState(false);

  const formRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const checkSub = async () => {
      setSubLoading(true);
      try {
        const subSnap = await getDoc(doc(db, "chargilySubscriptions", user.uid));
        if (subSnap.exists()) {
          const data = subSnap.data();
          setCredits(data.credits || 0);
          setTotalPaid(data.total_paid || 0);
          setHasEverPaid((data.total_paid || 0) > 0 || data.is_subscribed === true);
        }
      } catch (err) {
        console.error("❌ Erreur abonnement:", err);
      } finally {
        setSubLoading(false);
      }
    };
    checkSub();
  }, [user]);

  useEffect(() => {
    if (!user || !hasEverPaid) return;
    const fetchOffers = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, "immobilier"), where("promoteurId", "==", user.uid));
        const snap = await getDocs(q);
        setOffers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("❌ Erreur annonces:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [user, hasEverPaid]);

  const handleAddProperty = async (property) => {
    if (credits <= 0) {
      alert("Aucun crédit disponible. Effectuez un paiement pour publier une annonce.");
      return;
    }
    setProcessing(true);
    try {
      const { id, ...data } = property;
      const now = new Date();
      const expiresAt = new Date(now);
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);

      const docRef = await addDoc(collection(db, "immobilier"), {
        ...data,
        promoteurId: user.uid,
        userId: user.uid,
        userEmail: user.email,
        source: "particulier",
        status: "pending",
        createdAt: Timestamp.fromDate(now),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      await updateDoc(doc(db, "chargilySubscriptions", user.uid), {
        credits: credits - 1,
      });

      setCredits(prev => prev - 1);
      setOffers(prev => [...prev, {
        ...data, id: docRef.id,
        promoteurId: user.uid, status: "pending",
        createdAt: Timestamp.fromDate(now),
        expiresAt: Timestamp.fromDate(expiresAt),
      }]);
      setActiveTab("annonces");
    } catch (err) {
      console.error("❌ Erreur ajout:", err);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateProperty = async (updated) => {
    setProcessing(true);
    try {
      const { images, ...data } = updated;
      await updateDoc(doc(db, "immobilier", updated.id), data);
      setOffers(prev => prev.map(o => o.id === updated.id ? { ...o, ...data } : o));
    } catch (err) {
      console.error("❌ Erreur MAJ:", err);
    } finally {
      setProcessing(false);
      setIsEditing(false);
      setFormData(null);
      setActiveTab("annonces");
    }
  };

  const handleDeleteProperty = async (id) => {
    setProcessing(true);
    try {
      await deleteDoc(doc(db, "immobilier", id));
      setOffers(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      console.error("❌ Erreur suppression:", err);
    } finally {
      setProcessing(false);
    }
  };

  const handleEditProperty = (id) => {
    const property = offers.find(o => o.id === id);
    setFormData(property);
    setIsEditing(true);
    setActiveTab("nouvelle");
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  // ── Bouton payer réutilisable ─────────────────────────────────
  const BoutonPayer = () => (
    <button
      onClick={() => setShowPayment(true)}
      style={{ padding: "12px 28px", background: "#078D3F", color: "#fff", borderRadius: "10px", border: "none", fontWeight: "600", fontSize: "15px", cursor: "pointer" }}
    >
      💳 Payer pour publier
    </button>
  );

  // ── États spéciaux ───────────────────────────────────────────────

  if (subLoading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #e5e7eb", borderTop: "3px solid #078D3F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>Vérification...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Jamais payé → page paiement directement
  if (!hasEverPaid) {
    return <ChargilyPaymentPageClient />;
  }

  // 🆕 Page paiement depuis le dashboard
  if (showPayment) {
    return (
      <div>
        <div style={{ padding: "16px 20px" }}>
          <button
            onClick={() => setShowPayment(false)}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 16px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600", color: "#374151" }}
          >
            ← Retour à mon espace
          </button>
        </div>
        <ChargilyPaymentPageClient />
      </div>
    );
  }

  // ── Dashboard principal ──────────────────────────────────────────
  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "24px 20px", fontFamily: "system-ui, -apple-system, sans-serif" }}>

      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ margin: "0 0 4px", fontSize: "26px", fontWeight: "800", color: "#111827" }}>🏠 Mon Espace Client</h1>
        <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>Chaque paiement = 1 annonce pendant 1 an à partir de la date de publication.</p>
      </div>

      {/* Bannière crédit disponible */}
      {credits > 0 && (
        <div style={{ marginBottom: "20px", padding: "14px 18px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "14px", color: "#15803d", fontWeight: "600" }}>
            🎉 Vous avez {credits} crédit{credits > 1 ? "s" : ""} disponible{credits > 1 ? "s" : ""} — publiez votre annonce !
          </span>
          <button onClick={() => setActiveTab("nouvelle")} style={{ padding: "8px 18px", background: "#078D3F", color: "#fff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
            Publier →
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "28px", borderBottom: "1px solid #e5e7eb" }}>
        {[
          { key: "dashboard", label: "📊 Tableau de bord" },
          { key: "annonces", label: `📋 Mes annonces (${offers.length})` },
          ...(credits > 0 || isEditing ? [{ key: "nouvelle", label: isEditing ? "✏️ Modifier" : "➕ Nouvelle annonce" }] : []),
        ].map(tab => (
          <button key={tab.key}
            onClick={() => { setActiveTab(tab.key); if (tab.key !== "nouvelle") { setIsEditing(false); setFormData(null); } }}
            style={{
              padding: "10px 18px", border: "none", background: "none",
              fontSize: "14px", fontWeight: "600", cursor: "pointer",
              color: activeTab === tab.key ? "#078D3F" : "#6b7280",
              borderBottom: activeTab === tab.key ? "2px solid #078D3F" : "2px solid transparent",
              marginBottom: "-1px", transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab Dashboard ── */}
      {activeTab === "dashboard" && (
        <div>
          <AbonnementResume credits={credits} totalPaid={totalPaid} offers={offers} />

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px", marginBottom: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <h2 style={{ margin: "0 0 18px", fontSize: "17px", fontWeight: "700", color: "#111827" }}>💳 Mon compte</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
              {[
                { label: "Titulaire", value: user.displayName || user.email },
                { label: "Email", value: user.email },
                { label: "Crédits disponibles", value: `${credits} publication${credits > 1 ? "s" : ""}` },
                { label: "Total paiements", value: `${totalPaid} × 13 720 DA` },
                { label: "Annonces publiées", value: `${offers.length} annonce${offers.length > 1 ? "s" : ""}` },
                { label: "Plan", value: "1 paiement = 1 annonce / 1 an" },
              ].map(item => (
                <div key={item.label} style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>{item.label}</div>
                  <div style={{ fontSize: "14px", color: "#111827", fontWeight: "600" }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bloc payer si 0 crédits */}
          {credits === 0 && (
            <div style={{ textAlign: "center", padding: "30px", background: "#f9fafb", borderRadius: "20px", border: "2px dashed #e5e7eb", marginBottom: "24px" }}>
              <p style={{ margin: "0 0 16px", fontSize: "15px", color: "#6b7280" }}>
                Vous n'avez plus de crédit disponible.<br />Effectuez un nouveau paiement pour publier une annonce.
              </p>
              <BoutonPayer />
            </div>
          )}

          {/* Dernières annonces */}
          {offers.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "700", color: "#111827" }}>🏘️ Dernières annonces</h2>
                <button onClick={() => setActiveTab("annonces")} style={{ background: "none", border: "none", color: "#078D3F", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>Voir tout →</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {offers.slice(0, 3).map(offer => {
                  const createdAt = offer.createdAt?.toDate?.() || null;
                  const expiresAt = offer.expiresAt?.toDate?.() || expiryFromCreatedAt(createdAt);
                  const jours = joursRestants(expiresAt);
                  return (
                    <div key={offer.id} style={{ display: "flex", alignItems: "center", gap: "14px", padding: "12px 14px", background: "#f9fafb", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                      <div style={{ width: "44px", height: "44px", borderRadius: "8px", background: "#e5e7eb", overflow: "hidden", flexShrink: 0 }}>
                        {offer.mainImageUrl || offer.imagesUrls?.[0]
                          ? <img src={offer.mainImageUrl || offer.imagesUrls[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>🏠</div>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {offer.name || "Sans titre"}
                        </div>
                        <div style={{ fontSize: "12px", color: jours <= 30 ? "#dc2626" : "#6b7280" }}>
                          Expire le {formatDate(expiresAt)} · {jours} jours restants
                        </div>
                      </div>
                      <StatutBadge status={offer.status} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab Mes annonces ── */}
      {activeTab === "annonces" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}>Chargement...</div>
          ) : offers.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", background: "#f9fafb", borderRadius: "20px", border: "2px dashed #e5e7eb" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏠</div>
              <h3 style={{ margin: "0 0 8px", color: "#111827" }}>Aucune annonce publiée</h3>
              <p style={{ margin: "0 0 20px", color: "#6b7280", fontSize: "14px" }}>
                {credits > 0 ? "Vous avez un crédit disponible, publiez votre annonce !" : "Effectuez un paiement pour publier une annonce."}
              </p>
              {credits > 0
                ? <button onClick={() => setActiveTab("nouvelle")} style={{ padding: "10px 24px", background: "#078D3F", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>➕ Publier une annonce</button>
                : <BoutonPayer />
              }
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
              {offers.map(offer => (
                <AnnonceCard key={offer.id} offer={offer} onEdit={handleEditProperty} onDelete={handleDeleteProperty} processing={processing} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab Nouvelle annonce ── */}
      {activeTab === "nouvelle" && (
        <div ref={formRef}>
          {credits <= 0 && !isEditing ? (
            <div style={{ textAlign: "center", padding: "60px 20px", background: "#fef9c3", borderRadius: "20px", border: "2px solid #fde047" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
              <h3 style={{ margin: "0 0 8px", color: "#92400e" }}>Aucun crédit disponible</h3>
              <p style={{ margin: "0 0 20px", color: "#92400e", fontSize: "14px" }}>Effectuez un paiement pour publier une nouvelle annonce.</p>
              <BoutonPayer />
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <ImmoForm
                isEditing={isEditing}
                formData={formData}
                setFormData={setFormData}
                onAdd={handleAddProperty}
                onUpdate={handleUpdateProperty}
                processing={processing}
                addTitle="➕ Ajouter une nouvelle annonce"
                editTitle="✏️ Modifier l'annonce"
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ImmoDashboardClient;